import React, { useState, Children } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin } from 'lucide-react';
import { PageBanner } from '../components/PageBanner';
import { SoutheastAsiaMap } from '../components/SoutheastAsiaMap';
const organizations = [{
  name: "M'Lop Tapang",
  location: 'Sihanoukville, Cambodia',
  tags: ['Child Protection', 'Education', 'Healthcare'],
  description: "M'Lop Tapang supports vulnerable children, youth, and families in Sihanoukville, Cambodia. Its integrated programs include child protection and case management, non-formal education and school reintegration, healthcare access, and vocational training for youth helping thousands each year reach safety and stable livelihoods."
}, {
  name: 'Mith Samlanh',
  location: 'Phnom Penh, Cambodia',
  tags: ['Child Protection', 'Reintegration', 'Youth'],
  description: 'Mith Samlanh, a program of Friends International in Phnom Penh, Cambodia, reintegrating vulnerable children and youth through a cyclical process: outreach, drop-in centres, detoxification, transitional housing, education, and reintegration into school, work, or family. The model is non-linear, allowing re-entry at any stage, and combines crisis response with longer-term education and livelihood pathways. Cross-cutting services in child rights, life skills, HIV/AIDS, and medical care support children and youth throughout every stage.'
}, {
  name: 'Peuan Peuan',
  location: 'Bangkok and Pattaya, Thailand',
  tags: ['Youth', 'Protection', 'Outreach'],
  description: 'Peuan Peuan, a program of Friends-International in Thailand, works with vulnerable children, youth, and families in Bangkok and Pattaya. Its outreach teams identify children at risk and provide case management, child protection support, and referrals to education, healthcare, vocational training and social services. The program also offers employment support, and family assistance to help children stay safe and transition toward stable livelihoods.'
}, {
  name: 'Baan Dek Foundation',
  location: 'Chiang Mai and Bangkok, Thailand',
  tags: ['Child Protection', 'Community', 'Education'],
  description: "Baan Dek Foundation works with vulnerable children and families living in construction site camps in Chiang Mai and Bangkok. Its programs combine child protection, early childhood development, education access, and community health with initiatives that strengthen youth leadership and women's economic empowerment. Through the Building Social Impact Initiative, it also partners with construction companies to improve living conditions and services in worker camps."
}, {
  name: 'Stairway Foundation',
  location: 'Philippines',
  tags: ['Child Rights', 'Education', 'Cybersafety'],
  description: 'Stairway Foundation works to advance child protection and development across the Philippines. Its programs include residential care for at-risk boys, community education and livelihoods for indigenous groups, environmental training for youth, and nationwide e-learning for professionals in cybersafety and child protection combining direct services with systemic change.'
}, {
  name: 'Teach for the Philippines',
  location: 'Philippines',
  tags: ['Education', 'Leadership'],
  description: 'Teach for the Philippines expands access to quality education by developing teachers and school leaders within the public education system. Its Fellowship places young leaders in underserved classrooms for two years, while complementary programs strengthen school leadership, community engagement, and system collaboration. The organization works to build a pipeline of educators and changemakers improving learning outcomes across schools and communities.'
}, {
  name: 'Yayasan Chow Kit',
  location: 'Kuala Lumpur, Malaysia',
  tags: ['Child Protection', 'Youth', 'Advocacy'],
  description: 'Yayasan Chow Kit supports marginalized and at-risk children and youth in Kuala Lumpur, particularly in the Chow Kit area. Through drop-in centers, a youth hub, and a transitional shelter, it provides case management, education support, counselling, and safe spaces for children facing abuse, neglect, or homelessness. The organization also advocates for child rights and works to ensure vulnerable children can access identity, protection, education, and essential services.'
}];
export function WhoWeFundPage() {
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  const toggleCard = (index: number) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };
  const getShortDescription = (desc: string) => {
    const firstSentence = desc.split('. ')[0];
    return firstSentence.length > 100 ? firstSentence.substring(0, 100) + '...' : firstSentence + '.';
  };
  return <div className="pb-24" data-id="element-686">
      <PageBanner title="Who we fund" subtitle="Our focus areas" image="https://images.unsplash.com/photo-1555400038-63f5ba517a47?q=80&w=2070&auto=format&fit=crop" data-id="element-687" />

      {/* Child Protection Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-688">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-689">
          <div data-id="element-690">
            <h2 className="text-3xl font-serif text-brand-charcoal mb-6" data-id="element-691">
              Child Protection
            </h2>
            <p className="text-lg text-brand-brown leading-relaxed mb-6" data-id="element-692">
              Child protection is the foundation of the Firetree Fund portfolio.
              Effective protection is not a single service, nor a linear
              solution — it is an interconnected cycle: from stabilising
              immediate crisis through emergency social work and safe housing,
              to building futures through education, vocational training, and
              family reintegration — with healthcare and legal protection
              available as needed throughout.
            </p>
            <p className="text-lg text-brand-brown leading-relaxed mb-8" data-id="element-693">
              Each child moves through this cycle on their own terms, with a
              tailored plan built around their specific needs and circumstances.
              This approach is also the most effective defence against
              trafficking — every element builds the resilience, options, and
              support structures that make children less vulnerable to
              exploitation before it occurs.
            </p>
            <Link to="/child-protection" className="inline-flex items-center text-brand-red font-semibold hover:text-brand-charcoal transition-colors group" data-id="element-694">
              Read our perspective on child protection
              <ArrowRight className="ml-2 w-5 h-5 transform group-hover:translate-x-1 transition-transform" data-id="element-695" />
            </Link>
          </div>

          {/* Child Protection Cycle */}
          <div className="bg-brand-cream border border-brand-shape/50 p-8 lg:p-10 h-full" data-id="element-696">
            <p className="text-xs font-bold text-brand-gold uppercase tracking-[0.2em] mb-6" data-id="element-697">
              The protection cycle
            </p>
            <div className="space-y-3 mb-8" data-id="element-698">
              {[{
              num: '01',
              label: 'Outreach'
            }, {
              num: '02',
              label: 'Emergency Response'
            }, {
              num: '03',
              label: 'Safe Housing'
            }, {
              num: '04',
              label: 'Education & Training'
            }, {
              num: '05',
              label: 'Family Reintegration'
            }].map(service => <div key={service.label} className="flex items-center gap-4 py-3 border-b border-brand-shape/30 last:border-b-0" data-id="element-699">
                  <span className="text-sm font-serif text-brand-gold/60 w-6" data-id="element-700">
                    {service.num}
                  </span>
                  <span className="text-base font-semibold text-brand-charcoal" data-id="element-701">
                    {service.label}
                  </span>
                </div>)}
            </div>
            <div className="bg-brand-card/50 p-5" data-id="element-702">
              <p className="text-xs font-bold text-brand-gold uppercase tracking-[0.15em] mb-2" data-id="element-703">
                Cross-cutting services throughout
              </p>
              <p className="text-sm text-brand-brown leading-relaxed" data-id="element-704">
                Healthcare · Legal Protection · Life Skills · Rights &
                Participation
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Education Innovation Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-705">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start" data-id="element-706">
          <div data-id="element-707">
            <h2 className="text-3xl font-serif text-brand-charcoal mb-6" data-id="element-708">
              Education Innovation
            </h2>
            <p className="text-lg text-brand-brown leading-relaxed mb-6" data-id="element-709">
              While child protection anchors the Fund portfolio, children cannot
              be protected effectively if the public systems meant to serve them
              are failing. Education is the primary system through which
              vulnerable children are reached, monitored, and supported daily.
              When a school functions well, teachers become the first line of
              identification and response for children at risk.
            </p>
            <p className="text-lg text-brand-brown leading-relaxed mb-6" data-id="element-710">
              Teach for the Philippines works inside public schools across the
              country, placing trained fellows who deliver foundational literacy
              and life skills programmes in some of the most underserved
              communities. Like our protection-focused partners, TFP embeds
              practitioners within vulnerable geographies, coordinates formally
              with government through its MOU with the Department of Education,
              and builds local capacity that outlasts any single intervention.
            </p>
            <p className="text-lg text-brand-brown leading-relaxed" data-id="element-711">
              TFP's inclusion also reflects Firetree's direct operational
              experience in education. Through Starfish Education in Thailand,
              which Firetree fully funds and governs, we run school
              transformation programmes that develop teacher capacity and
              foundational literacy inside public schools — work that is
              structurally parallel to TFP's. As with child protection, our
              practitioner knowledge informs how we assess, support, and engage
              with TFP as a partner organization.
            </p>
          </div>

          {/* Imagery column */}
          <div className="flex flex-col gap-4" data-id="element-712">
            <div className="overflow-hidden rounded-2xl" data-id="element-713">
              <img src="https://cdn.magicpatterns.com/uploads/svuqCvjzYKpdsqKhxyHAry/DSCN3897.jpg" alt="Children in a classroom in Southeast Asia" className="w-full h-64 lg:h-72 object-cover" data-id="element-714" />
            </div>
            <div className="overflow-hidden rounded-2xl" data-id="element-715">
              <img src="https://cdn.magicpatterns.com/uploads/pFKH3vwEguyebNTbFmxX1T/P1140460.jpg" alt="Students learning together" className="w-full h-48 lg:h-56 object-cover" data-id="element-716" />
            </div>
          </div>
        </div>
      </section>

      {/* Image Breaker */}
      <div className="w-full" data-id="element-717">
        <div className="relative h-48 md:h-72 w-full overflow-hidden" data-id="element-718">
          <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
          backgroundImage: 'url("https://cdn.magicpatterns.com/uploads/oycjRBLuBGVUMSr6Xgqnpb/FromTerri_Cambodia2.jpg")'
        }} data-id="element-719" />
          <div className="absolute inset-0 bg-brand-charcoal/10 mix-blend-multiply" data-id="element-720" />
        </div>
      </div>

      {/* The Organisations Section */}
      <section className="py-24 bg-brand-card/30" data-id="element-721">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-722">
          <div className="max-w-3xl mb-16" data-id="element-723">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-724">
              The organisations
            </h2>
            <p className="text-lg text-brand-brown leading-relaxed" data-id="element-725">
              The portfolio is not fixed. As our relationships deepen and
              organisations from our broader portfolio meet all the criteria in
              our assessment rubric, we will grow the portfolio. All additions
              are approved by the Firetree Board, advised by a regional panel of
              technical experts from across Southeast Asia chaired by Nicky
              Wilkinson, ED FPF.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-726">
            {organizations.map((org, index) => {
            const isExpanded = expandedCards.has(index);
            return <div key={org.name} onClick={() => toggleCard(index)} className="bg-brand-cream p-8 rounded-2xl shadow-sm border border-brand-shape/50 flex flex-col h-full cursor-pointer transition-all duration-300 hover:shadow-md hover:border-brand-shape" data-id="element-727">
                  <div className="mb-4" data-id="element-728">
                    <h3 className="text-xl font-serif text-brand-charcoal mb-2" data-id="element-729">
                      {org.name}
                    </h3>
                    <div className="flex items-center text-sm text-brand-gold font-medium mb-3" data-id="element-730">
                      <MapPin className="w-4 h-4 mr-1" data-id="element-731" />
                      {org.location}
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-732">
                      {org.tags.map(tag => <span key={tag} className="text-xs font-medium px-3 py-1 rounded-full bg-brand-red/10 text-brand-red" data-id="element-733">
                          {tag}
                        </span>)}
                    </div>
                  </div>
                  <div className="flex-grow" data-id="element-734">
                    <p className="text-brand-brown leading-relaxed mb-1" data-id="element-735">
                      {getShortDescription(org.description)}
                    </p>
                    <div className="grid transition-all duration-500 ease-in-out" style={{
                  gridTemplateRows: isExpanded ? '1fr' : '0fr'
                }} data-id="element-736">
                      <div className="overflow-hidden" data-id="element-737">
                        <p className="text-brand-brown leading-relaxed pt-2" data-id="element-738">
                          {org.description.substring(org.description.indexOf('. ') + 2)}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 text-sm text-brand-red font-semibold inline-flex items-center gap-1" data-id="element-739">
                      {isExpanded ? 'Read less' : 'Read more'}
                      <ArrowRight className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`} data-id="element-740" />
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* Southeast Asia Map */}
      <section className="py-16 md:py-24 bg-brand-cream" data-id="element-741">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-742">
          <div className="text-center mb-12" data-id="element-743">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-4" data-id="element-744">
              Our presence across Southeast Asia
            </h2>
            <p className="text-lg text-brand-brown" data-id="element-745">
              Working in four countries with deep local partnerships
            </p>
          </div>
          <SoutheastAsiaMap data-id="element-746" />
        </div>
      </section>
    </div>;
}